import React, { FC, useCallback, useMemo } from 'react';
import classNames from 'classnames';

import { PAGE_HEADER_HEIGHT } from '~/app/components/PageHeader';
import toSizedImageUrlNext from '~/app/lib/toSizedImageUrlNext';
import Image, { ImageProps } from '~/app/components/Image';
import SingleColumnLayout from '../SingleColumnLayout';
import Box, { BoxProps } from '~/app/components/Box';
import { toVhValue } from '~/app/components/Vh';
import { useI18n } from '~/app/lib/i18n';

import { MobileSingleColumnLayoutProps } from './types';

const SIZED_IMAGE_WIDTH = 750;
const IMAGE_QUALITY = 60;
const SPACE_BETWEEN_SECTIONS = '3.9rem';

const MobileSingleColumnLayout: FC<MobileSingleColumnLayoutProps> = ({
  image,
  testId,
  ...singleColumnLayoutProps
}) => {
  const { t } = useI18n();

  const sizedImageUrl =
    image?.url &&
    toSizedImageUrlNext({
      url: image.url,
      width: SIZED_IMAGE_WIDTH,
      quality: IMAGE_QUALITY,
    });

  return (
    <SingleColumnLayout
      {...singleColumnLayoutProps}
      contentStyle={{ padding: `0 0 10rem` }}
      spaceBetweenSections={SPACE_BETWEEN_SECTIONS}
      testId={classNames('mobileSingleColumnLayout', testId)}
      renderBackground={useCallback(
        ({ imageRef }) => {
          const customProps: {
            container?: Partial<BoxProps>;
            image?: Partial<ImageProps>;
          } = (() => {
            switch (image.layout.type) {
              case 'fit':
                return {
                  container: {
                    height: '100vw',
                  },

                  image: {
                    cover: true,
                  },
                };

              case 'fade':
                return {
                  container: {
                    height: toVhValue(68),
                  },

                  image: {
                    style: {
                      WebkitMaskImage: `linear-gradient(rgba(0,0,0,.9) 0%, rgba(0,0,0,.9) 40%, rgba(0,0,0,0) 100%)`,
                    },
                  },
                };

              default:
                return {};
            }
          })();

          return (
            <Box fullHeight {...customProps.container} nodeRef={imageRef}>
              <Image
                testId="mobileLayoutImage"
                fullHeight
                fullWidth
                cover
                src={sizedImageUrl}
                alt={t('item.pageBackgroundImageAlt')}
                aspect={
                  image?.width && image.height
                    ? image?.width / image?.height
                    : undefined
                }
                fadeInDuration={400}
                imgStyle={{
                  filter: 'saturate(.92)',
                  opacity: 0.9,
                  ...customProps.image?.imgStyle,
                }}
                {...customProps.image}
              />
            </Box>
          );
        },
        [image]
      )}
      spaceAboveContent={useMemo(() => {
        switch (image.layout.type) {
          case 'fit':
            return '100vw';

          case 'fade':
            return {
              none: PAGE_HEADER_HEIGHT,
              medium: `calc(${PAGE_HEADER_HEIGHT} + 84vw)`,
              large: toVhValue(70),
            }[image.layout.spaceAboveContent];
        }
      }, [image])}
    />
  );
};

export default MobileSingleColumnLayout;
